/**
 * Mock data for capacity management development and testing
 *
 * This provides realistic test data when backend endpoints are unavailable.
 */

import { addDays } from 'date-fns';
import { ShiftData, VisitData, CalendarBlockData, ProviderData } from '../types/capacity.types';

const today = new Date();
today.setHours(0, 0, 0, 0);

/**
 * Mock provider data
 *
 * 12 providers across 3 roles (RN, PT, OT)
 * Utilization varies by day of week to demonstrate heatmap:
 * - Mondays: Very busy (over capacity - RED)
 * - Tuesdays: Busy (tight capacity - ORANGE)
 * - Wednesdays: Tight capacity (ORANGE)
 * - Thursdays: Good capacity (GREEN)
 * - Fridays: Good capacity (GREEN)
 */
export const mockProviders: ProviderData[] = [
  { id: 1, name: 'Andy Harrison', role: 'RN' },      // High util, large panel
  { id: 2, name: 'Dipa Mehta', role: 'RN' },       // Very high util, very large panel
  { id: 3, name: 'Frank Langdon', role: 'RN' },    // Medium-high util
  { id: 4, name: 'Heather Collins', role: 'RN' },          // Medium util
  { id: 5, name: 'Hillary Swank', role: 'RN' },  // Low util, small panel
  { id: 6, name: 'Pete Burns', role: 'RN' },      // Low util
  { id: 7, name: 'Lesli Knope', role: 'PT' },      // Medium util
  { id: 8, name: 'Tiffany Yu', role: 'PT' },    // Medium-high util
  { id: 9, name: 'Kyle Tatz', role: 'PT' },  // Medium util PT
  { id: 10, name: 'Randy Johnson', role: 'PT' },    // High util PT
  { id: 11, name: 'Annie Case', role: 'OT' },      // Low util OT
  { id: 12, name: 'Hong Truong', role: 'OT' },        // Medium util OT
];

/**
 * Generate mock shifts for providers
 */
export function generateMockShifts(): ShiftData[] {
  const shifts: ShiftData[] = [];

  // Generate shifts for 30 days
  for (let day = 0; day < 30; day++) {
    const date = addDays(today, day);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    mockProviders.forEach((provider) => {
      // Most providers work Monday-Friday (day 1-5)
      // Exclude weekends (Saturday = 6, Sunday = 0)
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const startTime = new Date(date);
        startTime.setHours(8, 0, 0, 0);

        const endTime = new Date(date);
        endTime.setHours(17, 0, 0, 0); // 9-hour shift

        shifts.push({
          id: shifts.length + 1,
          providerId: provider.id,
          startTime,
          endTime,
          status: 'confirmed',
        });
      }
    });
  }

  return shifts;
}

/**
 * Generate mock visits with varied utilization levels
 *
 * Creates different utilization patterns per provider AND per day to show heatmap variation:
 * - Some days have very high utilization (over capacity - RED)
 * - Some days have tight capacity (80-90% - ORANGE)
 * - Some days have good capacity (under 80% - GREEN)
 */
export function generateMockVisits(): VisitData[] {
  const visits: VisitData[] = [];
  const shifts = generateMockShifts();

  shifts.forEach((shift) => {
    const dayOfWeek = shift.startTime.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayNumber = Math.floor((shift.startTime.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    let numVisits: number;
    let visitDurationBase: number;

    // Base utilization by provider
    let baseVisits: number;
    let baseDuration: number;

    switch (shift.providerId) {
      case 1: // RN - High utilization
        baseVisits = 5;
        baseDuration = 85;
        break;
      case 2: // RN - Very high utilization
        baseVisits = 6;
        baseDuration = 90;
        break;
      case 3: // RN - Medium-high utilization
        baseVisits = 4;
        baseDuration = 85;
        break;
      case 4: // RN - Medium utilization
        baseVisits = 4;
        baseDuration = 75;
        break;
      case 5: // RN - Low utilization (best)
        baseVisits = 2;
        baseDuration = 70;
        break;
      case 6: // RN - Low-medium utilization
        baseVisits = 3;
        baseDuration = 75;
        break;
      case 7: // RN - Medium utilization
        baseVisits = 4;
        baseDuration = 80;
        break;
      case 8: // RN - Medium-high utilization
        baseVisits = 5;
        baseDuration = 80;
        break;
      case 9: // PT - Medium utilization
        baseVisits = 4;
        baseDuration = 75;
        break;
      case 10: // PT - High utilization
        baseVisits = 5;
        baseDuration = 85;
        break;
      case 11: // OT - Low utilization (best for OT)
        baseVisits = 2;
        baseDuration = 75;
        break;
      case 12: // OT - Medium utilization
        baseVisits = 4;
        baseDuration = 80;
        break;
      default:
        baseVisits = 3;
        baseDuration = 70;
    }

    // Create varied patterns across providers - not all busy on same days
    // Each provider has their own "busy days" pattern based on provider ID

    // Determine this provider's busy days (provider-specific pattern)
    const providerBusyPattern = shift.providerId % 5; // Creates 5 different patterns

    let dayModifier = 0;
    let durationModifier = 0;

    // Pattern 0: Busy Mondays & Wednesdays
    if (providerBusyPattern === 0) {
      if (dayOfWeek === 1) { // Monday - RED
        dayModifier = 4;
        durationModifier = 30;
      } else if (dayOfWeek === 3) { // Wednesday - ORANGE
        dayModifier = 2;
        durationModifier = 15;
      } else if (dayOfWeek === 5) { // Friday - GREEN
        dayModifier = -2;
        durationModifier = -20;
      }
    }
    // Pattern 1: Busy Tuesdays & Thursdays
    else if (providerBusyPattern === 1) {
      if (dayOfWeek === 2) { // Tuesday - RED
        dayModifier = 4;
        durationModifier = 30;
      } else if (dayOfWeek === 4) { // Thursday - ORANGE
        dayModifier = 2;
        durationModifier = 15;
      } else if (dayOfWeek === 1) { // Monday - GREEN
        dayModifier = -2;
        durationModifier = -20;
      }
    }
    // Pattern 2: Busy Wednesdays & Fridays
    else if (providerBusyPattern === 2) {
      if (dayOfWeek === 3) { // Wednesday - RED
        dayModifier = 4;
        durationModifier = 30;
      } else if (dayOfWeek === 5) { // Friday - ORANGE
        dayModifier = 2;
        durationModifier = 15;
      } else if (dayOfWeek === 2) { // Tuesday - GREEN
        dayModifier = -2;
        durationModifier = -20;
      }
    }
    // Pattern 3: Busy Mondays & Thursdays
    else if (providerBusyPattern === 3) {
      if (dayOfWeek === 1) { // Monday - RED
        dayModifier = 4;
        durationModifier = 30;
      } else if (dayOfWeek === 4) { // Thursday - ORANGE
        dayModifier = 2;
        durationModifier = 15;
      } else if (dayOfWeek === 3) { // Wednesday - GREEN
        dayModifier = -2;
        durationModifier = -20;
      }
    }
    // Pattern 4: Busy Tuesdays only, lighter rest of week
    else {
      if (dayOfWeek === 2) { // Tuesday - RED
        dayModifier = 4;
        durationModifier = 30;
      } else if (dayOfWeek === 1 || dayOfWeek === 3) { // Mon/Wed - ORANGE
        dayModifier = 2;
        durationModifier = 15;
      } else { // Thu/Fri - GREEN
        dayModifier = -1;
        durationModifier = -15;
      }
    }

    numVisits = Math.max(2, baseVisits + dayModifier);
    visitDurationBase = baseDuration + durationModifier;

    // Add random capacity crunches on specific days for specific providers
    // This creates scattered RED spots across the calendar
    const providerCrunchDays = [
      [3, 11, 18, 25],  // Provider pattern 0
      [5, 13, 21, 28],  // Provider pattern 1
      [7, 14, 22, 29],  // Provider pattern 2
      [4, 12, 19, 26],  // Provider pattern 3
      [6, 15, 23, 27],  // Provider pattern 4
    ];

    const crunchDays = providerCrunchDays[shift.providerId % 5];
    if (crunchDays.includes(dayNumber)) {
      numVisits += 3; // Extra busy days (RED)
      visitDurationBase += 25;
    }

    // High-demand providers (2, 10) have additional pressure on random days
    if ((shift.providerId === 2 || shift.providerId === 10) && (dayNumber % 7 === shift.providerId % 7)) {
      numVisits += 2;
      visitDurationBase += 20;
    }

    for (let i = 0; i < numVisits; i++) {
      const visitDuration = visitDurationBase + Math.floor(Math.random() * 20);
      const startOffset = i * 90; // Space visits 1.5 hours apart

      const visitStart = new Date(shift.startTime);
      visitStart.setMinutes(visitStart.getMinutes() + startOffset);

      const visitEnd = new Date(visitStart);
      visitEnd.setMinutes(visitEnd.getMinutes() + visitDuration);

      // Only add if within shift hours
      if (visitEnd <= shift.endTime) {
        visits.push({
          id: visits.length + 1,
          eventStatus: 'scheduled',
          visitResources: [
            {
              providerId: shift.providerId,
              startTime: visitStart,
              endTime: visitEnd,
            },
          ],
        });
      }
    }
  });

  return visits;
}

/**
 * Generate mock calendar blocks (breaks)
 */
export function generateMockCalendarBlocks(): CalendarBlockData[] {
  const blocks: CalendarBlockData[] = [];
  const shifts = generateMockShifts();

  // Add lunch break for each shift
  shifts.forEach((shift) => {
    const lunchStart = new Date(shift.startTime);
    lunchStart.setHours(12, 0, 0, 0);

    const lunchEnd = new Date(lunchStart);
    lunchEnd.setMinutes(lunchEnd.getMinutes() + 30); // 30-minute lunch

    blocks.push({
      id: blocks.length + 1,
      providerId: shift.providerId,
      startTime: lunchStart,
      endTime: lunchEnd,
      blockType: 'break',
      eventStatus: 'scheduled',
    });
  });

  return blocks;
}

/**
 * Get current FTE counts by role from mock providers
 *
 * Counts the number of providers per role (1 provider = 1 FTE)
 */
export function getMockCurrentFTE(): { [role: string]: number } {
  const fteCounts: { [role: string]: number } = {};

  mockProviders.forEach((provider) => {
    if (!fteCounts[provider.role]) {
      fteCounts[provider.role] = 0;
    }
    fteCounts[provider.role] += 1.0;
  });

  return fteCounts;
}

/**
 * Mock current FTE counts by role
 *
 * These represent the current staffing levels for scenario planning
 */
export const mockCurrentFTE = getMockCurrentFTE();

/**
 * Get all mock data in one call
 */
export function getMockCapacityData() {
  return {
    providers: mockProviders,
    shifts: generateMockShifts(),
    visits: generateMockVisits(),
    calendarBlocks: generateMockCalendarBlocks(),
  };
}
